import { createFileRoute } from '@tanstack/react-router'
import { Shield, FileText, MapPin, Accessibility } from 'lucide-react'

import { Tabs, TabsList, TabsTrigger, TabsContent } from '@components/ui/tabs'
import { Separator } from '@components/ui/separator'
import { Heading } from '@components/ui/typography/heading'
import { Text } from '@components/ui/typography/text'
import { VStack, Flex, Spacer } from '@components/ui/layout/flex'

import ClinicAllyLogo from '@components/images/clinically-logo.png'
import People from '@components/images/abobo-together.svg'
import Terms from '@components/TermsAndConditions/Terms'
import PrivacyPolicy from '@components/TermsAndConditions/PrivacyPolicy'
import AccessibilityStatement from '@components/TermsAndConditions/AccessibilityStatement'
import CASupplement from '@components/TermsAndConditions/CASupplement'
import { useBranding } from '@utils/api'

export const Route = createFileRoute('/privacy')({
  component: Privacy,
})

function Privacy() {
  const { branding } = useBranding()

  return (
    <div className="animate-in fade-in duration-300">
      <div className="py-10">
        <div className="max-w-5xl mx-auto bg-white p-12 rounded-xl shadow-lg">
          <VStack gap={4} align="stretch">
            <Flex
              className="py-2 flex-col md:flex-row items-center md:items-start text-center md:text-left"
            >
              <Heading as="h1" level={2} className="border-none pb-0">Your Privacy Matters.</Heading>
              <Spacer />
              <div className="pl-0 md:pl-10 w-[200px] md:w-[225px] mt-4 md:mt-0">
                <img alt="My ClinicAlly Logo" src={ClinicAllyLogo} />
              </div>
            </Flex>
            <Separator />
            <Text className="text-gray-800 text-xl font-medium">
              {branding?.name} values your privacy. They have partnered with My
              ClinicAlly to help protect your privacy.
            </Text>

            <Text className="text-gray-700 text-lg font-light">
              Learn how My ClinicAlly uses and discloses the health information
              we collect from you and how you can access the health information
              we collect in our Privacy Policy.
            </Text>
            <Text className="text-gray-700 text-lg font-light">
              Learn what we expect from you as you use this service in our Terms
              of Use.
            </Text>
            <Text className="text-gray-700 text-lg font-light">
              Learn about our commitment to accessibility in our Accessibility
              Statement.
            </Text>

            <div className="max-w-[60%] self-center">
              <img alt="People holding hands" src={People} />
            </div>

            <div className="w-full">
              <Tabs defaultValue="privacy">
                <TabsList className="w-full bg-transparent grid grid-cols-2 md:grid-cols-4 gap-3 p-0 h-auto">
                  <TabsTrigger
                    value="privacy"
                    className="flex flex-col items-center gap-2 px-4 py-4 rounded-lg border-2 border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 transition-all data-[active]:border-[#3D2732] data-[active]:bg-[#3D2732] data-[active]:text-white data-[active]:shadow-md"
                  >
                    <Shield size={20} />
                    <span className="text-sm font-medium">Privacy Policy</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="terms"
                    className="flex flex-col items-center gap-2 px-4 py-4 rounded-lg border-2 border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 transition-all data-[active]:border-[#CA4618] data-[active]:bg-[#CA4618] data-[active]:text-white data-[active]:shadow-md"
                  >
                    <FileText size={20} />
                    <span className="text-sm font-medium">Terms of Use</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="california"
                    className="flex flex-col items-center gap-2 px-4 py-4 rounded-lg border-2 border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 transition-all data-[active]:border-[#CA4618] data-[active]:bg-[#CA4618] data-[active]:text-white data-[active]:shadow-md"
                  >
                    <MapPin size={20} />
                    <span className="text-sm font-medium">California Rights</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="accessibility"
                    className="flex flex-col items-center gap-2 px-4 py-4 rounded-lg border-2 border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 transition-all data-[active]:border-[#562323] data-[active]:bg-[#562323] data-[active]:text-white data-[active]:shadow-md"
                  >
                    <Accessibility size={20} />
                    <span className="text-sm font-medium">Accessibility</span>
                  </TabsTrigger>
                </TabsList>
                <Separator className="my-6" />
                <div className="bg-white">
                  <TabsContent value="privacy">
                    <PrivacyPolicy />
                  </TabsContent>
                  <TabsContent value="terms">
                    <Terms />
                  </TabsContent>
                  <TabsContent value="california">
                    <CASupplement />
                  </TabsContent>
                  <TabsContent value="accessibility">
                    <AccessibilityStatement />
                  </TabsContent>
                </div>
              </Tabs>
            </div>
          </VStack>

          <Separator />
        </div>
      </div>
    </div>
  )
}
